import useSWR from 'swr';
import moment from 'moment-timezone';
import axiosInstance from 'apis/axiosInstance';
import { loadSelectedClinicId } from 'utils/storage';
import { OnlineSchedulingParams } from 'interfaces/onlineSchedulingParams';
import { DATE_TIME_FORMAT } from 'utils/datetime';
import { CANCEL_REASON_CODE_WITH_DESCRIPTION } from 'utils/constants';
import { formatFullPhoneNumber } from 'utils/formatPhoneNumber';
import { TRAFFIC_SOURCES } from 'pages/OnlineSchedulingReportPage/TrafficBySource/TRAFFIC_SOURCES';

const REQUESTED_TIME_FORMAT = 'MMM DD, YYYY hh:mm A Z [UTC]';
const APPT_DATE_FORMAT = DATE_TIME_FORMAT.MMM_DD_YYYY;
const APPT_TIME_INPUT_FORMATS = [DATE_TIME_FORMAT.HH_MM_SS, 'HH:mm A'];
const APPT_TIME_FORMAT = DATE_TIME_FORMAT.HH_MM_A;

export type MetricKey =
  | 'appointment-requested'
  | 'appointment-accepted'
  | 'appointment-completed'
  | 'appointment-cancelled'
  | 'new-patient'
  | 'existing-patient'
  | 'production';

export interface MetricBreakdownDetail {
  label: string;
  value: number;
  color?: string;
}

export interface MetricBreakdownTableRow {
  id: string;
  [columnId: string]: string | number;
}

export interface MetricBreakdownPeriodData {
  total: number;
  details: MetricBreakdownDetail[];
  rows: MetricBreakdownTableRow[];
}

export interface MetricBreakdownData {
  periodData: MetricBreakdownPeriodData;
}

export interface TotalAppointmentByMetricValue {
  serviceName?: string;
  cancelReasonCode?: string;
  totalAppointment?: number;
  totalProduction?: number;
}

export interface AppointmentByMetricValue {
  id: string;
  cancelReasonCode?: string | null;
  appointmentDate: string;
  startTime: string;
  patientName: string;
  patientPhoneNumber?: string | null;
  doctorName: string;
  linkedAppointmentDoctorName?: string | null;
  linkedAppointmentStartTime?: string | null;
  createdAt: string;
  timezone: string;
  serviceName: string;
  source?: string | null;
}

export interface RoiAppointmentMetricResponse {
  totals: TotalAppointmentByMetricValue[];
  appointments: AppointmentByMetricValue[];
}

interface RoiAppointmentMetricBreakdownParams extends OnlineSchedulingParams {
  metric: MetricKey;
  source?: string;
}

const getCancelReason = (code?: string | null) =>
  CANCEL_REASON_CODE_WITH_DESCRIPTION.find((reason) => reason.code === code);

const buildDetails = (
  metric: MetricKey,
  totals: TotalAppointmentByMetricValue[]
): MetricBreakdownDetail[] => {
  if (metric === 'appointment-cancelled') {
    return CANCEL_REASON_CODE_WITH_DESCRIPTION.map((reason) => {
      const total = totals.find((t) => t.cancelReasonCode === reason.code);
      return {
        label: reason.description,
        color: reason.color,
        value: total?.totalAppointment ?? 0,
      };
    });
  }

  return totals.map((total) => ({
    label: total.serviceName ?? '',
    value:
      metric === 'production'
        ? (total.totalProduction ?? 0)
        : (total.totalAppointment ?? 0),
  }));
};

const buildRows = (
  appointments: AppointmentByMetricValue[]
): MetricBreakdownTableRow[] =>
  appointments.map((appointment) => {
    const apptDateTime = moment(appointment.appointmentDate);
    const startTime = moment(appointment.startTime, APPT_TIME_INPUT_FORMATS);
    const linkedStartTime = appointment.linkedAppointmentStartTime
      ? moment(appointment.linkedAppointmentStartTime, APPT_TIME_INPUT_FORMATS)
      : null;
    const apptTime =
      linkedStartTime?.isValid() && linkedStartTime.isBefore(startTime)
        ? linkedStartTime
        : startTime;
    if (apptTime.isValid()) {
      apptDateTime.set({
        hour: apptTime.hour(),
        minute: apptTime.minute(),
        second: apptTime.second(),
      });
    }

    const reason = getCancelReason(appointment.cancelReasonCode);

    return {
      id: appointment.id,
      apptDate: apptDateTime.format(APPT_DATE_FORMAT),
      apptTime: apptTime.format(APPT_TIME_FORMAT),
      apptDateTime: apptDateTime.valueOf(),
      patient: appointment.patientName,
      patientPhoneNumber: appointment.patientPhoneNumber
        ? formatFullPhoneNumber(`+${appointment.patientPhoneNumber}`)
        : '',
      practitioner: appointment.doctorName,
      linkedAppointmentDoctorName:
        appointment.linkedAppointmentDoctorName ?? '',
      service: appointment.serviceName,
      source:
        TRAFFIC_SOURCES.find(({ key }) => key === appointment.source)?.label ||
        '—',
      rejectedReason: reason?.description ?? appointment.cancelReasonCode ?? '',
      rejectedReasonColor: reason?.color ?? '',
      requestedTime: moment(appointment.createdAt)
        .tz(appointment.timezone)
        .format(REQUESTED_TIME_FORMAT),
      requestedDateTime: moment(appointment.createdAt).valueOf(),
    };
  });

const transformBreakdown = (
  metric: MetricKey,
  response: RoiAppointmentMetricResponse
): MetricBreakdownData => {
  const details = buildDetails(metric, response.totals);
  return {
    periodData: {
      total: details.reduce((sum, detail) => sum + detail.value, 0),
      details,
      rows: buildRows(response.appointments),
    },
  };
};

const useRoiAppointmentMetricBreakdown = (
  params: RoiAppointmentMetricBreakdownParams
) => {
  const storedId = loadSelectedClinicId();
  const clinicIds = params.clinicIds ?? [storedId];
  const query =
    params.startDate && params.endDate
      ? new URLSearchParams({
          startDate: params.startDate,
          endDate: params.endDate,
          hourType: params.hourType,
          metric: params.metric,
          ...(params.source ? { source: params.source } : {}),
        }).toString()
      : '';

  const url =
    params.startDate && params.endDate
      ? `/rois/appointment/metrics?${query}`
      : null;

  const { data, error, isValidating, mutate } = useSWR(
    url ? [url, clinicIds] : null,
    async (...resource) => {
      const [url] = resource;
      const response = await axiosInstance.get<RoiAppointmentMetricResponse>(
        url,
        {
          params: { clinicIds },
        }
      );
      return transformBreakdown(params.metric, response.data);
    }
  );

  if (!params.startDate || !params.endDate) {
    return {
      data: null,
      error: undefined,
      isLoading: false,
      isValidating: false,
      mutate: async () => {},
    };
  }

  return {
    data,
    error,
    isLoading: !data && !error,
    isValidating,
    mutate,
  };
};

export default useRoiAppointmentMetricBreakdown;
